import { useEffect, useRef } from 'react';
import { signalRService } from '../services/signalrService';

interface UseSignalROptions {
  enabled?: boolean;
  accessToken?: string;
}

export const useSignalR = (options: UseSignalROptions = {}) => {
  const { enabled = true, accessToken } = options;
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!enabled || !accessToken || isInitialized.current) {
      return;
    }

    const initializeSignalR = async () => {
      try {
        await signalRService.start(accessToken);
        isInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize SignalR:', error);
      }
    };

    initializeSignalR();

    // Cleanup on unmount
    return () => {
      if (isInitialized.current) {
        signalRService.stop();
        isInitialized.current = false;
      }
    };
  }, [enabled, accessToken]);

  return {
    on: signalRService.on.bind(signalRService),
    off: signalRService.off.bind(signalRService),
    subscribeToTicket: signalRService.subscribeToTicket.bind(signalRService),
    unsubscribeFromTicket: signalRService.unsubscribeFromTicket.bind(signalRService),
    sendTypingIndicator: signalRService.sendTypingIndicator.bind(signalRService),
    isConnected: signalRService.isConnectionActive.bind(signalRService)
  };
};

// Hook for listening to specific notification types
export const useNotification = (
  eventType: string,
  handler: (data: any) => void,
  deps: any[] = []
) => {
  useEffect(() => {
    signalRService.on(eventType, handler);

    return () => {
      signalRService.off(eventType, handler);
    };
  }, [eventType, ...deps]);
};

// Hook for subscribing to a specific ticket
export const useTicketSubscription = (ticketId: string | undefined) => {
  useEffect(() => {
    if (!ticketId) return;

    const subscribe = async () => {
      await signalRService.subscribeToTicket(ticketId);
    };

    subscribe();

    return () => {
      if (ticketId) {
        signalRService.unsubscribeFromTicket(ticketId);
      }
    };
  }, [ticketId]);
};
